import React from 'react';
import {Dialog, DialogType, List, PrimaryButton, Spinner, Stack, TextField, Text, MessageBar, MessageBarType} from '@fluentui/react';
import {debounce} from 'lodash';
import {ICrmImportCompany, useLazySearchFolioCompaniesQuery} from '../../store/endpoints/RecordLinksEndpoints';

interface ICompanyLinkDialogProps {
    /** The folio (CRM) to search and link against. When undefined the dialog is hidden. */
    folioId?: string;
    folioName?: string;
    /** Term to auto-search on open (the company's identifier, else its name). Shown as the placeholder. */
    initialSearch?: string;
    onClose: () => void;
    /** Called with the chosen CRM company's external id when the user picks a candidate. */
    onLink: (externalId: string) => void;
}

/** One line of a CRM company candidate (city/state, identifier) for disambiguation. */
const companySubtitle = (c: ICrmImportCompany): string => {
    const place = [c.city, c.stateTerritory].filter(Boolean).join(', ');
    return [c.identifier ? `#${c.identifier}` : undefined, place || undefined].filter(Boolean).join(' · ');
};

/**
 * Search a CRM (folio) for companies by name/identifier and let the user pick one to link to the current
 * Adaptive Catalog company. Mirrors the CRM company search in ExtensionCompanyFieldSearch, surfaced as a
 * pick-from-results modal so similarly-named companies can be told apart.
 */
const CompanyLinkDialog: React.FC<ICompanyLinkDialogProps> = (props) => {
    const [search, setSearch] = React.useState<string>('');
    const [runSearch, {data, isFetching}] = useLazySearchFolioCompaniesQuery();

    const debouncedSearch = React.useMemo(
        () => debounce((folioId: string, value: string) => { void runSearch({folioId, search: value}); }, 400, {leading: false}),
        [runSearch]
    );

    React.useEffect(() => {
        // On open: clear the box and auto-search the company's identifier/name so likely matches show up front.
        if (!props.folioId) return;
        setSearch('');
        const seed = props.initialSearch?.trim();
        if (seed) void runSearch({folioId: props.folioId, search: seed});
    }, [props.folioId, props.initialSearch, runSearch]);

    const onSearchChange = (value?: string) => {
        setSearch(value ?? '');
        // Search what the user typed; fall back to the seed term when they clear the box.
        const term = (value ?? '').trim() || (props.initialSearch ?? '').trim();
        if (props.folioId && term.length > 0) debouncedSearch(props.folioId, term);
    };

    const companies = data?.companies ?? [];

    return (
        <Dialog
            hidden={!props.folioId}
            onDismiss={props.onClose}
            dialogContentProps={{type: DialogType.normal, title: `Link with ${props.folioName ?? 'CRM'}`}}
            modalProps={{isBlocking: false}}
            minWidth={480}
        >
            <Stack tokens={{childrenGap: '0.5em'}}>
                <TextField
                    label='Search by company name or identifier'
                    value={search}
                    placeholder={props.initialSearch || 'Company name or identifier'}
                    autoFocus
                    onChange={(_e, v) => onSearchChange(v)}
                />
                {isFetching && <Spinner label='Searching…' />}
                {data && !data.success && <MessageBar messageBarType={MessageBarType.error}>{data.error ?? 'Search failed'}</MessageBar>}
                {!isFetching && data?.success && companies.length === 0 &&
                    <Text>No matching companies found.</Text>}
                {companies.length > 0 &&
                    <div style={{maxHeight: 320, overflowY: 'auto'}}>
                        <List
                            items={companies}
                            onRenderCell={(c?: ICrmImportCompany) => c?.externalId ? (
                                <Stack
                                    horizontal
                                    verticalAlign='center'
                                    tokens={{childrenGap: '1em'}}
                                    style={{padding: '8px 6px', borderBottom: '1px solid #edebe9'}}
                                >
                                    <Stack.Item grow>
                                        <Text block styles={{root: {fontWeight: 600}}}>{c.name ?? c.externalId}</Text>
                                        {companySubtitle(c) && <Text block variant='small'>{companySubtitle(c)}</Text>}
                                    </Stack.Item>
                                    <PrimaryButton
                                        text='Link'
                                        iconProps={{iconName: 'Link'}}
                                        onClick={() => props.onLink(c.externalId ?? '')}
                                    />
                                </Stack>
                            ) : null}
                        />
                    </div>}
            </Stack>
        </Dialog>
    );
};

export default CompanyLinkDialog;
